import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type { GuestRatingSummary } from '@apps/main/new-desktop/modules/customersRating';

import { DateFormat, SemboDate } from '@libs/sembo-dates';
import type {
  ContentApiImage,
  DestinationStaticInfo,
  GuestRatingBreakdown,
  HotelBookableOptionWithPriceBreakdown,
  HotelIngredientResponse,
  OverviewDestinationStaticInfo,
} from '@libs/trip-planner-interfaces';

import type {
  PeekPriceHotelCardData,
  PeekPriceModalHotelOnlyItinerary,
  PeekPriceRoomDetails,
} from '../../models';
import { PeekPriceModalItineraryType } from '../../models';

const { t } = i18n.global;

export function calculateDates(
  displayDate: Date | undefined,
  totalNights: number
): string | undefined {
  if (!displayDate) {
    return;
  }

  const startDate = new SemboDate(displayDate);
  const endDate = new SemboDate(displayDate).add({
    days: totalNights,
  });

  const sameMonth =
    startDate.format(DateFormat.MonthFullName) ===
    endDate.format(DateFormat.MonthFullName);

  return sameMonth
    ? `${startDate.format(DateFormat.Day)} - ${endDate.format(
        DateFormat.Day
      )} ${endDate.format(DateFormat.FullMonthAndYear)}`
    : `${startDate.format(DateFormat.Day)} ${startDate.format(
        DateFormat.MonthFullName
      )} - ${endDate.format(DateFormat.Day)} ${endDate.format(
        DateFormat.FullMonthAndYear
      )}`;
}

export function getHotelSelectionCardSubtitle(
  product: DestinationStaticInfo | OverviewDestinationStaticInfo
): string {
  if (product.mainPolygon) {
    return product.mainPolygon.fullName;
  }

  if (product?.address?.city) {
    return product.address.country
      ? `${product.address.city}, ${product.address.country}`
      : product.address.city;
  }

  return '';
}

interface PeekPriceModalHotelOnlyItineraryParams {
  displayDate: Date | undefined;
  paxes: string;
  hotelAvail: HotelIngredientResponse | undefined;
  hotelStaticInfo: OverviewDestinationStaticInfo | DestinationStaticInfo;
  getImageUrl: (file: ContentApiImage) => string;
  formatMoney: (money: number) => string;
  getGuestRating: (
    guestRatings: GuestRatingBreakdown | undefined
  ) => GuestRatingSummary | undefined;
}

export function getPeekPriceModalHotelOnlyItinerary(
  params: PeekPriceModalHotelOnlyItineraryParams
): PeekPriceModalHotelOnlyItinerary {
  const imagesLimit = 3;
  const amount = params.hotelAvail?.suggested?.totalPrice.amount;
  const price = amount ? params.formatMoney(amount) : undefined;
  const rooms: PeekPriceRoomDetails[] | undefined =
    params?.hotelAvail?.suggested?.rooms.map(
      (room: HotelBookableOptionWithPriceBreakdown) => ({
        description: room.description,
        boardBasisCode: room.boardBasis,
      })
    );
  const numberOfNights = params.hotelAvail?.numberOfNights;
  const dates = numberOfNights
    ? calculateDates(params.displayDate, numberOfNights)
    : '';
  const hotel: PeekPriceHotelCardData = {
    dates,
    imageUrls: params.hotelStaticInfo.images
      .slice(0, imagesLimit)
      .map((image: ContentApiImage) => params.getImageUrl(image)),
    nights: numberOfNights
      ? `${numberOfNights} ${t('planner.nights', numberOfNights)}`
      : '',
    hotelName: params.hotelStaticInfo.name,
    hotelDestination: getHotelSelectionCardSubtitle(params.hotelStaticInfo),
    hotelStars: params.hotelStaticInfo.semboSunRating,
    guestRating: params.getGuestRating(params.hotelStaticInfo.guestRatings),
    rooms,
  };

  const hotelOnlyItinerary: PeekPriceModalHotelOnlyItinerary = {
    type: PeekPriceModalItineraryType.HotelOnly,
    dates,
    price,
    paxes: params.paxes,
    hotel,
  };

  return hotelOnlyItinerary;
}
